/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from 'src/components/ui/Icon/Icon';
import classNames from 'classnames';
import { t } from 'i18next';
import Badge, { BadgeType } from '../../ui/Badge/Badge';
import styles from './TabBar.module.scss';
import ConfirmationModal from '../../modals/ConfirmationModal/ConfirmationModal';

export interface TabProps {
  label: string;
  badgeNumber?: number;
  key?: string;
  content?: React.ReactNode;
  entriesColor?: BadgeType;
  isRemovable?: boolean;
  onClick?: () => void;
  hasError?: boolean;
}

interface TabBarProps {
  tabs: TabProps[];
  className?: string;
  defaultIndex?: number;
  onActiveTabChange?: (index: number) => void;
  confirmOnTabChange?: () => boolean;
}

const TabBar: React.FC<TabBarProps> = ({
  tabs: initialTabs,
  className = '',
  defaultIndex,
  onActiveTabChange,
  confirmOnTabChange = () => false,
}) => {
  const [tabs, setTabs] = useState(initialTabs);
  const [activeTab, setActiveTab] = useState(defaultIndex || 0);
  const [pendingTab, setPendingTab] = useState<number | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setTabs(initialTabs);
  }, [initialTabs]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam) {
      const foundIndex = tabs.findIndex(t => t.key === tabParam);
      if (foundIndex !== -1 && foundIndex !== activeTab) {
        setActiveTab(foundIndex);
      }
    }
  }, [location.search, tabs]);

  useEffect(() => {
    const updateIndicatorStyle = () => {
      const currentTab = tabRefs.current[activeTab];
      if (currentTab) {
        const { offsetLeft, offsetWidth } = currentTab;
        const hasActiveTabError = tabs[activeTab]?.hasError;
        setIndicatorStyle({
          transform: `translateX(${offsetLeft}px)`,
          width: offsetWidth,
          backgroundColor: hasActiveTabError ? 'var(--status-error-full)' : undefined,
        });
      }
    };
    updateIndicatorStyle();
    window.addEventListener('resize', updateIndicatorStyle);
    return () => window.removeEventListener('resize', updateIndicatorStyle);
  }, [activeTab, tabs]);

  const switchTab = (index: number) => {
    const selectedTab = tabs[index];
    if (selectedTab?.key) {
      const params = new URLSearchParams(location.search);
      params.set('tab', selectedTab.key);
      navigate({ search: params.toString() }, { replace: true });
    }
    setActiveTab(index);
    if (selectedTab.onClick) selectedTab.onClick();
  };

  const handleTabClick = (index: number) => {
    if (index === activeTab) return;

    if (confirmOnTabChange && confirmOnTabChange()) {
      setPendingTab(index);
      setIsConfirmOpen(true);
    } else {
      switchTab(index);
    }
  };

  const handleConfirmClose = (confirmed: boolean) => {
    setIsConfirmOpen(false);
    if (confirmed && pendingTab !== null) {
      switchTab(pendingTab);
    }
    setPendingTab(null);
  };

  useEffect(() => {
    onActiveTabChange?.(activeTab);
  }, [activeTab, onActiveTabChange]);

  return (
    <div className={classNames(styles.root, className)}>
      <div className="flex relative gap-x-24">
        {tabs.map((tab, index) => (
          <div className="flex flex-shrink-0 pb-17" key={tab.label}>
            <div
              ref={el => {
                tabRefs.current[index] = el;
              }}
              role="none"
              className={`flex cursor-pointer typo-text-16 ${
                tab.hasError ? 'status-error-full' : activeTab === index ? 'text-primary' : 'text-faded'
              }`}
              onClick={() => handleTabClick(index)}>
              {tab.label}
              {!!tab?.badgeNumber && tab?.badgeNumber > 0 && (
                <div className="pl-6">
                  <Badge expanded dotFilled label={String(tab?.badgeNumber)} type={tab.entriesColor} />
                </div>
              )}
              {tab.isRemovable && (
                <button
                  type="button"
                  aria-label="removeTab"
                  className="pl-6"
                  onClick={() => setTabs(tabs.filter((_, i) => i !== index))}>
                  <Icon name="deleteCircle" />
                </button>
              )}
            </div>
          </div>
        ))}
        <div className={styles.indicator} style={indicatorStyle} />
      </div>

      {isConfirmOpen && (
        <ConfirmationModal
          isOpen={isConfirmOpen}
          title={t('confirm_close_modal.tab_title')}
          description={t('confirm_close_modal.tab_info')}
          onClose={handleConfirmClose}
        />
      )}
    </div>
  );
};

export default TabBar;
